import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Close16 from '@carbon/icons-react/es/close/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { Tooltip } from 'antd';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ifProp, prop, switchProp } from 'styled-tools';
import { Button } from 'components/facade/button';
import { queueMacrotask } from 'utils/macrotask';
import { IconWrapper } from '../icon-shell/icon-shell';
import { Height, HeightStyle, InputFocus, InputStyle, Input } from './input';
const SearchButton = styled(Button).attrs({
    icon: /*#__PURE__*/ _jsx(IconWrapper, {
        children: /*#__PURE__*/ _jsx(Search16, {})
    })
})`
  :root[data-new-theme-enabled='true'] & {
    &:hover,
    &:active,
    &:focus {
      border-radius: var(--border-radius-small);
    }
  }
`;
const CloseButton = styled(Button).attrs({
    icon: /*#__PURE__*/ _jsx(Close16, {})
})`
  :root[data-new-theme-enabled='true'] & {
    &:hover,
    &:active,
    &:focus {
      border-radius: var(--border-radius-small);
    }
  }
`;
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  transition: all 0.11s cubic-bezier(0.2, 0, 0.38, 0.9);
  max-width: ${HeightStyle};
  overflow: hidden;

  ${ifProp('active', css`
      ${InputStyle};
      max-width: 100%;
      padding: 0;
      &:focus-within {
        ${InputFocus};
      }

      :root[data-new-theme-enabled='true'] & {
        outline: none;
        border-radius: var(--border-radius-small);
        background-color: var(--white-100);
        border: 1px solid var(--gray-20);
        &:focus-within {
          border: 2px solid var(--gray-20);
        }
      }
      ${SearchButton} {
        pointer-events: none;
      }
    `)};

  ${Input} {
    padding: 0;
    height: calc(${HeightStyle} - 6px);
    border: none;
    outline: none;
  }

  :root[data-new-theme-enabled='true'] & {
    ${Input} {
      background-color: transparent;
      color: var(--black-100);
    }
  }
`;
const AnimationWrapper = styled.div`
  position: relative;
  min-width: ${(props)=>{
    var _props_minWidth;
    return (_props_minWidth = props.minWidth) !== null && _props_minWidth !== void 0 ? _props_minWidth : '180px';
}};
  width: ${(props)=>{
    var _props_width;
    return (_props_width = props.width) !== null && _props_width !== void 0 ? _props_width : '100%';
}};
  ${Height};
  ${Wrapper} {
    position: ${(props)=>props.expanded ? 'static' : 'absolute'};
    ${switchProp(prop('direction', 'left'), {
    left: css`
        left: 0px;
      `,
    right: css`
        right: 0px;
      `
})};
  }
`;
export const SearchInput = /*#__PURE__*/ forwardRef(({ controlled, expanded = false, onChange, onBlur, onFocus, onFocusBeforeActive, onClose, size = 'middle', direction = 'left', className, tooltip, searchButtonProps = {
    type: 'default'
}, minWidth, width, ...props }, ref)=>{
    var _props_value, _ref;
    const inputRef = useRef(null);
    const [active, _setActive] = useState(expanded);
    const [value, setValue] = useState(controlled ? (_props_value = props.value) !== null && _props_value !== void 0 ? _props_value : '' : '');
    const setActive = useCallback((_active)=>{
        if (!expanded) {
            _setActive(_active);
        }
    }, [
        expanded
    ]);
    const openSearchInput = useCallback(()=>{
        setActive(true);
        queueMacrotask(()=>{
            var _inputRef_current;
            (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
        });
    }, [
        setActive
    ]);
    const onChangeValue = (e)=>{
        setValue(e.target.value);
        onChange === null || onChange === void 0 ? void 0 : onChange(e);
    };
    const refCallback = useCallback((element)=>{
        inputRef.current = element;
        if (ref) {
            if (typeof ref === 'function') {
                ref(element);
            } else {
                ref.current = element;
            }
        }
    }, [
        ref
    ]);
    useEffect(()=>{
        if (controlled) {
            setValue(props.value || '');
        }
    }, [
        props.value,
        controlled
    ]);
    return /*#__PURE__*/ _jsx(AnimationWrapper, {
        active: active,
        expanded: expanded,
        size: size,
        direction: direction,
        className: className,
        minWidth: minWidth,
        width: width,
        children: /*#__PURE__*/ _jsxs(Wrapper, {
            active: active,
            size: size,
            className: "search-input-wrapper",
            children: [
                /*#__PURE__*/ _jsx(Tooltip, {
                    title: tooltip,
                    children: /*#__PURE__*/ _jsx(SearchButton, {
                        size: size,
                        type: active ? 'text' : searchButtonProps.type,
                        disabled: (_ref = searchButtonProps === null || searchButtonProps === void 0 ? void 0 : searchButtonProps.disabled) !== null && _ref !== void 0 ? _ref : false,
                        tabIndex: active ? -1 : 0,
                        onFocus: openSearchInput,
                        onClick: openSearchInput,
                        "data-cy": "search-input.button"
                    })
                }),
                /*#__PURE__*/ _jsx(Input, {
                    ...props,
                    tabIndex: active ? 0 : -1,
                    ref: refCallback,
                    value: value,
                    onChange: onChangeValue,
                    onFocus: (e)=>{
                        onFocusBeforeActive === null || onFocusBeforeActive === void 0 ? void 0 : onFocusBeforeActive(e);
                        setActive(true);
                        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
                    },
                    onBlur: (e)=>{
                        if (!e.target.value) {
                            setActive(false);
                        }
                        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
                    },
                    "data-cy": "search.text-field"
                }),
                !expanded || value ? /*#__PURE__*/ _jsx(CloseButton, {
                    "data-cy": "search-input.close-button",
                    type: "text",
                    size: size,
                    tabIndex: -1,
                    onClick: (e)=>{
                        setActive(false);
                        const event = Object.create(e);
                        const target = inputRef.current.input;
                        const currentTarget = target.cloneNode(true);
                        event.target = currentTarget;
                        event.currentTarget = currentTarget;
                        currentTarget.value = '';
                        onChangeValue(event);
                        onClose === null || onClose === void 0 ? void 0 : onClose(event);
                    }
                }) : null
            ]
        })
    });
});
